import React, { useState } from 'react';
import { FaCalendarAlt, FaUsers, FaClock, FaArrowRight } from 'react-icons/fa';
import THEME from '../utils/theme';
import { STATUS_LABEL, truncate } from '../utils/helpers';
import SectionTag from '../components/SectionTag';
import Loader from '../components/Loader';
import useFetch from '../hooks/useFetch';
import useInView from '../hooks/useInView';

const STATUS_STYLE = {
  upcoming:  { bg: '#E8F4FF', color: '#1A6FBF', dot: '#3B82F6' },
  ongoing:   { bg: THEME.greenLight, color: THEME.green, dot: THEME.green },
  completed: { bg: '#F0F0F0', color: '#666', dot: '#999' },
};

const FILTERS = [
  { key: 'all',       label: 'All Batches' },
  { key: 'upcoming',  label: 'Upcoming'    },
  { key: 'ongoing',   label: 'Ongoing'     },
  { key: 'completed', label: 'Completed'   },
];

export default function Batches() {
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const { data: batches, loading } = useFetch(`${API_BASE}/batches`);
  const [ref, visible] = useInView();
  const [filter, setFilter] = useState('all');

  const filtered = filter === 'all' ? batches : batches.filter(b => b.status === filter);

  return (
    <section id="batches" style={{ padding: '100px clamp(20px,4vw,40px)', background: THEME.ivory }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>

        <div ref={ref} className={`sk-fade-up ${visible ? 'sk-visible' : ''}`}>
          <SectionTag>Batch Schedule</SectionTag>
          <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 'clamp(2rem,4vw,3.2rem)', lineHeight: 1.1, color: THEME.navy }}>
            Current & Upcoming Batches
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0 36px' }}>
            <span style={{ fontFamily: "'Noto Serif', serif", fontSize: 13, color: `${THEME.red}80`, letterSpacing: '0.1em' }}>
              バッチを確認してください
            </span>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${THEME.red}40, transparent)` }} />
          </div>
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 36, flexWrap: 'wrap' }}>
          {FILTERS.map(t => (
            <button key={t.key} onClick={() => setFilter(t.key)} style={{
              padding: '8px 20px', borderRadius: 4, cursor: 'pointer',
              border: '1.5px solid', transition: 'all 0.2s',
              borderColor: filter === t.key ? THEME.red : '#DDD',
              background: filter === t.key ? THEME.red : 'white',
              color: filter === t.key ? 'white' : THEME.navy,
              fontFamily: "'Syne', sans-serif", fontWeight: 600, fontSize: 13,
            }}>{t.label}</button>
          ))}
        </div>

        {loading ? <Loader /> : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 80, background: 'white', borderRadius: 12, border: '1px solid #EDE7DE' }}>
            <FaCalendarAlt size={48} color="#CCC" style={{ display: 'block', margin: '0 auto 12px' }} />
            <p style={{ color: '#999' }}>No batches available at the moment</p>
          </div>
        ) : (
          /* grid — alignItems stretch for equal height */
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 24, alignItems: 'stretch' }}>
            {filtered.map((b, i) => {
              const ss   = STATUS_STYLE[b.status] || STATUS_STYLE.upcoming;
              const seatLeft = b.totalSeats - (b.enrolledCount || 0);
              const pct  = Math.min(100, Math.round(((b.enrolledCount || 0) / b.totalSeats) * 100));

              return (
                <div key={b._id}
                  className={`sk-card-hover sk-fade-up ${visible ? 'sk-visible' : ''}`}
                  style={{
                    transitionDelay: `${i * 0.07}s`,
                    background: 'white', borderRadius: 14,
                    border: '1px solid #EDE7DE', overflow: 'hidden',
                    display: 'flex', flexDirection: 'column', height: '100%',
                  }}>

                  {/* ── Batch image — 16:9 ratio, img tag ── */}
                  {b.imageUrl ? (
                    <div style={{
                      position: 'relative',
                      width: '100%',
                      paddingTop: '56.25%',   /* 16:9 */
                      overflow: 'hidden',
                      flexShrink: 0,
                      background: '#f0f0f0',
                    }}>
                      <img
                        src={b.imageUrl}
                        alt={b.batchName}
                        loading="lazy"
                        style={{
                          position: 'absolute',
                          top: 0, left: 0,
                          width: '100%', height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center',
                          display: 'block',
                        }}
                      />
                      {/* Status badge over image */}
                      <div style={{
                        position: 'absolute', top: 12, right: 12,
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        background: ss.bg, borderRadius: 20, padding: '4px 12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      }}>
                        <div style={{
                          width: 7, height: 7, borderRadius: '50%', background: ss.dot,
                          animation: b.status === 'ongoing' ? 'pulseDot 1.5s infinite' : 'none',
                        }} />
                        <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 11, fontWeight: 700, color: ss.color, letterSpacing: '0.06em' }}>
                          {STATUS_LABEL[b.status] || b.status}
                        </span>
                      </div>
                    </div>
                  ) : (
                    /* No image — compact header with gradient */
                    <div style={{
                      height: 80, flexShrink: 0,
                      background: `linear-gradient(135deg, ${THEME.navy} 0%, #1a2d42 100%)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '0 20px',
                    }}>
                      <FaCalendarAlt size={28} color="rgba(255,255,255,0.18)" />
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        background: ss.bg, borderRadius: 20, padding: '4px 12px',
                      }}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: ss.dot }} />
                        <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 11, fontWeight: 700, color: ss.color }}>
                          {STATUS_LABEL[b.status] || b.status}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* ── Card body ── */}
                  <div style={{ padding: '22px 24px 26px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 700, color: THEME.navy, flex: 1, paddingRight: 8, lineHeight: 1.3 }}>
                        {b.batchName}
                      </h3>
                      {/* Show status badge here too when no image */}
                      {!b.imageUrl && (
                        <span style={{
                          background: ss.bg, color: ss.color, fontSize: 11, fontWeight: 700,
                          padding: '4px 10px', borderRadius: 2, letterSpacing: '0.06em',
                          flexShrink: 0, fontFamily: "'Syne', sans-serif",
                        }}>{STATUS_LABEL[b.status] || b.status}</span>
                      )}
                    </div>

                    {b.description && (
                      <p style={{ fontSize: 13, color: '#7A8090', lineHeight: 1.6, marginBottom: 14 }}>
                        {truncate(b.description, 80)}
                      </p>
                    )}

                    {/* Info grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                      {[
                        { l: 'Start Date',  v: new Date(b.startDate).toLocaleDateString('en-GB') },
                        { l: 'Duration',    v: b.duration   },
                        { l: 'Total Seats', v: b.totalSeats },
                        { l: 'Seats Left',  v: seatLeft     },
                      ].map(r => (
                        <div key={r.l} style={{ background: THEME.sand, padding: '8px 12px', borderRadius: 6 }}>
                          <div style={{ fontSize: 10, color: '#999', marginBottom: 2, fontWeight: 600 }}>{r.l}</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: THEME.navy }}>{r.v}</div>
                        </div>
                      ))}
                    </div>

                    {/* Progress bar — flex pushes CTA to bottom */}
                    <div style={{ marginBottom: 18, marginTop: 'auto' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#999', marginBottom: 4 }}>
                        <span>Seat Availability</span>
                        <span style={{ fontWeight: 700, color: pct > 80 ? THEME.red : THEME.green }}>{pct}% filled</span>
                      </div>
                      <div style={{ height: 6, background: '#F0EDE8', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', width: `${pct}%`,
                          background: pct > 80 ? THEME.red : THEME.green,
                          borderRadius: 3, transition: 'width 1s ease',
                        }} />
                      </div>
                    </div>

                    {b.status !== 'completed' && (
                      <a href="#apply" style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        background: THEME.red, color: 'white',
                        padding: '11px 20px', borderRadius: 6, fontSize: 13,
                        fontFamily: "'Syne', sans-serif", fontWeight: 700,
                        textDecoration: 'none', letterSpacing: '0.04em',
                        transition: 'background 0.2s, transform 0.15s',
                        boxShadow: `0 6px 18px ${THEME.red}35`,
                      }}
                        onMouseEnter={e => { e.currentTarget.style.background = THEME.redDark; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = THEME.red; e.currentTarget.style.transform = 'translateY(0)'; }}>
                        Apply for This Batch <FaArrowRight size={13} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulseDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.5; transform: scale(1.5); }
        }
      `}</style>
    </section>
  );
}
